import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IActiveLeads } from 'services/DashboardApi/types';
import { theme } from '../../../../../tailwind.config';
import classnames from 'classnames';
import { ErrorBar } from 'ui/NetworkStatusBar';
import { MetricCard, MetricCardTitle } from './MetricCardComponents';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import { HidingTextTooltip } from 'ui/Tooltip/HidingTextTooltip';

interface IActiveLeadsCardProps {
  activeLeads: IActiveLeads;
  isErrorCrmMetrics: boolean;
}

interface ILeadSegmentContentProps {
  label: string;
  value: number;
}

const getTruncatedLabel = (label: string, width: number, element: HTMLElement) => {
  if (width <= 0) {
    return { text: label, isTruncated: false };
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    return { text: label, isTruncated: false };
  }

  const computedStyle = window.getComputedStyle(element);
  context.font = `${computedStyle.fontStyle} ${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;

  const ellipsis = '...';
  const fullLabelWidth = context.measureText(label).width;
  if (fullLabelWidth <= width) {
    return { text: label, isTruncated: false };
  }

  const ellipsisWidth = context.measureText(ellipsis).width;
  const firstChar = label.charAt(0);
  const firstCharWithEllipsis = `${firstChar}${ellipsis}`;
  const firstCharWithEllipsisWidth = context.measureText(firstCharWithEllipsis).width;

  if (firstCharWithEllipsisWidth > width) {
    if (ellipsisWidth <= width) {
      return { text: ellipsis, isTruncated: true };
    }

    return { text: firstCharWithEllipsis, isTruncated: true };
  }

  let low = 1;
  let high = label.length;
  let bestPrefixLength = 1;

  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const candidate = `${label.slice(0, middle)}${ellipsis}`;
    const candidateWidth = context.measureText(candidate).width;

    if (candidateWidth <= width) {
      bestPrefixLength = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  return { text: `${label.slice(0, bestPrefixLength)}${ellipsis}`, isTruncated: true };
};

const LeadSegmentContent: React.FC<ILeadSegmentContentProps> = ({ label, value }) => {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [displayLabel, setDisplayLabel] = useState(label);
  const [isLabelTruncated, setIsLabelTruncated] = useState(false);

  useEffect(() => {
    const labelElement = labelRef.current;
    if (!labelElement) {
      return;
    }

    const updateLabelState = () => {
      const { text, isTruncated } = getTruncatedLabel(label, labelElement.clientWidth, labelElement);
      setDisplayLabel(text);
      setIsLabelTruncated(isTruncated);
    };

    updateLabelState();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateLabelState();
    });
    resizeObserver.observe(labelElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [label]);

  return (
    <div className="flex flex-col items-stretch justify-center w-full min-w-0 px-[6px]">
      <HidingTextTooltip
        enabled={isLabelTruncated}
        tooltipContent={`${label}: ${value}`}
        position="top"
        tooltipContentClassname="pt-[1px] px-[5px] pb-[3px]"
        elementClassname="w-full min-w-0 justify-center"
      >
        <span ref={labelRef} className="block w-full max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-center">
          {displayLabel}
        </span>
      </HidingTextTooltip>
      <span className="text-center">{value}</span>
    </div>
  );
};

export const ActiveLeadsCard: React.FC<IActiveLeadsCardProps> = ({ activeLeads, isErrorCrmMetrics }) => {
  const { isMobile, isTablet, isDesktop, isDesktopHD } = useResponsive();
  const segmentClassname = classnames('flex items-center justify-center rounded-tl-[4px] rounded-bl-[4px] min-w-0', {
    'min-w-[40px]': isMobile || isTablet,
    'min-w-[50px]': isDesktop || isDesktopHD,
  });
  
  const renderTooltipContent = useCallback(
    () => (
      <p className="m-0 font-pt-sans text-[13px] text-black p-[5px]">
        Summary of Sales agent's current number of leads per status.
      </p>
    ),
    []
  );

  return (
    <MetricCard>
      <MetricCardTitle title="Active Leads" renderTooltipContent={renderTooltipContent} />

      {isErrorCrmMetrics ? (
        <div className="w-full flex justify-center items-center mt-[30px] mb-[30px]">
          <ErrorBar />
        </div>
      ) : (
        <>
      <div className="flex flex-col gap-[5px] items-center justify-center w-full">
        <p className="m-0 font-pt-sans text-[15px] text-black">Current Active Leads</p>
        <p className="m-0 mb-[8px] font-pt-sans font-bold text-[21px] text-black text-center w-full">{activeLeads.total}</p>
      </div>

      <div className="flex h-[75px] items-stretch w-full">
        <div
          className={segmentClassname}
          style={{ backgroundColor: theme.colors['status-enquiry'], flex: activeLeads.discovery || 1 }}
        >
          <div className="font-pt-sans text-[17px] text-white text-center leading-[1.3] w-full">
            <LeadSegmentContent label="Discovery" value={activeLeads.discovery} />
          </div>
        </div>
        <div
          className={segmentClassname}
          style={{ backgroundColor: theme.colors['status-requested'], flex: activeLeads.nurture || 1 }}
        >
          <div className="font-pt-sans text-[17px] text-white text-center leading-[1.3] w-full">
            <LeadSegmentContent label="Nurture" value={activeLeads.nurture} />
          </div>
        </div>
      </div>
        </>
      )}
    </MetricCard>
  );
};
